import { useState, useEffect, useMemo, useCallback } from 'react';
import { useConfirmDialog } from '../hooks/useConfirmDialog';
import { useSelector } from 'react-redux';
import { RolesForm } from '../components/RolesForm';
import { DataTable } from '../components/DataTable';
import { DateCell } from '../components/DateCell';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Role, RolePayload } from '../features/roles/types';
import {
  loadRoles,
  addRole,
  editRole,
  removeRole,
  setRolesSearch,
} from '../features/roles/rolesSlice';
import { RootState } from '../store/store';
import { useAppDispatch } from '../store/hooks';
import { createColumnHelper } from '@tanstack/react-table';

const columnHelper = createColumnHelper<Role>();

const columns = [
  columnHelper.accessor('name', {
    header: 'Nombre del Rol',
    cell: (info) => info.getValue(),
  }),
  columnHelper.accessor('createdAt', {
    header: 'Creado',
    cell: (info) => <DateCell value={info.getValue()} />,
  }),
  columnHelper.display({
    id: 'actions',
    header: 'Acciones',
    cell: () => (
      <div className="flex gap-2">
        <button className="btn btn-secondary btn-sm">Editar</button>
        <button className="btn btn-danger btn-sm">Eliminar</button>
      </div>
    ),
  }),
];

function RoleActionsCell({ role, onEdit, onDelete }: {
  role: Role;
  onEdit: (item: Role) => void;
  onDelete: (id: string) => void;
}) {
  return (
    <div className="flex gap-2">
      <button
        className="inline-flex items-center justify-center rounded-md px-3 py-1 text-sm font-medium bg-secondary text-secondary-foreground hover:bg-secondary/80 transition-colors"
        onClick={() => onEdit(role)}
      >
        Editar
      </button>
      <button
        className="inline-flex items-center justify-center rounded-md px-3 py-1 text-sm font-medium bg-destructive text-destructive-foreground hover:bg-destructive/90 transition-colors"
        onClick={() => onDelete(role.id)}
      >
        Eliminar
      </button>
    </div>
  );
}

export function RolesPage() {
  const { confirm } = useConfirmDialog();
  const dispatch = useAppDispatch();
  const {
    items,
    loading,
    error,
    pagination,
    search
  } = useSelector((state: RootState) => state.roles);

  const [formOpen, setFormOpen] = useState(false);
  const [editingItem, setEditingItem] = useState<Role | null>(null);
  const [formError, setFormError] = useState<string>('');

  useEffect(() => {
    dispatch(loadRoles({
      page: pagination.page,
      pageSize: pagination.pageSize,
      search
    }));
  }, [dispatch, pagination.page, pagination.pageSize, search]);

  const closeForm = () => {
    setFormOpen(false);
    setEditingItem(null);
    setFormError('');
  };

  const openCreate = () => {
    setEditingItem(null);
    setFormError('');
    setFormOpen(true);
  };

  const handleSubmit = (values: RolePayload) => {
    try {
      setFormError('');
      if (editingItem) {
        dispatch(editRole({ id: editingItem.id, payload: values }));
      } else {
        dispatch(addRole(values));
      }
      closeForm();
    } catch (error: any) {
      setFormError(error?.response?.data?.error || 'Error al guardar el rol');
    }
  };

  const handleEdit = useCallback((item: Role) => {
    setEditingItem(item);
    setFormError('');
    setFormOpen(true);
  }, []);

  const handleDelete = useCallback(async (id: string) => {
    const ok = await confirm({
      title: 'Eliminar rol',
      message: '¿Estás seguro de que quieres eliminar este rol?',
      confirmText: 'Eliminar',
      variant: 'danger',
    });
    if (!ok) return;
    try {
      dispatch(removeRole(id));
    } catch (error) {
      console.error('Error deleting role:', error);
    }
  }, [confirm, dispatch]);

  const actionsCellRenderer = useCallback((info: any) => (
    <RoleActionsCell role={info.row.original} onEdit={handleEdit} onDelete={handleDelete} />
  ), [handleEdit, handleDelete]);

  const tableColumns = useMemo(() => columns.map(col => ({
    ...col,
    cell: col.id === 'actions' ? actionsCellRenderer : col.cell,
  })), [actionsCellRenderer]);

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h1 className="text-2xl font-bold">Roles</h1>
        <button className="btn btn-primary" onClick={openCreate}>
          Nuevo Rol
        </button>
      </div>

      <div>
        <div className="max-w-md">
          <label className="block text-sm font-medium text-foreground mb-1">Buscar roles</label>
          <input
            type="text"
            className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm focus:border-primary focus:outline-none text-foreground placeholder:text-muted-foreground"
            placeholder="Buscar por nombre..."
            value={search}
            onChange={(e) => dispatch(setRolesSearch(e.target.value))}
          />
        </div>
      </div>

      <div>
        {error && (
          <div className="mb-4 p-3 bg-destructive/10 border border-destructive/20 rounded text-destructive">
            {error}
          </div>
        )}
        <DataTable
          columns={tableColumns}
          data={items}
          loading={loading}
          emptyMessage="No hay roles registrados"
          pageSizeOptions={[10, 20, 50]}
        />
      </div>

      <Dialog open={formOpen} onOpenChange={(open) => !open && closeForm()}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>{editingItem ? 'Editar Rol' : 'Nuevo Rol'}</DialogTitle>
          </DialogHeader>
          <RolesForm
            initialValues={editingItem}
            onSubmit={handleSubmit}
            onCancel={closeForm}
            error={formError}
            onNameChange={(value) => {
              if (value.length > 160) {
                setFormError('El nombre no puede exceder 160 caracteres');
              } else {
                setFormError('');
              }
            }}
          />
        </DialogContent>
      </Dialog>
    </div>
  );
}
